import { useState, useEffect, useCallback, useRef } from "react";
import { requestNotificationPermission } from "../services/firebase";

const useNotifications = () => {
  const [token, setToken] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const processedNotificationsRef = useRef(new Set());
  const initializationAttempted = useRef(false);

  const userToken = localStorage.getItem("Mandoobee-token");
  const baseUrl =
    process.env.NODE_ENV === "development"
      ? process.env.REACT_APP_BASE_URL_DEV
      : process.env.REACT_APP_BASE_URL_PROD;

  // Production environment validation
  const validateProductionEnvironment = useCallback(() => {
    const checks = {
      https: window.location.protocol === 'https:' || window.location.hostname === 'localhost',
      serviceWorkerSupport: 'serviceWorker' in navigator,
      notificationSupport: 'Notification' in window,
      pushManagerSupport: 'PushManager' in window,
      vapidSupport: 'serviceWorker' in navigator && 'PushManager' in window,
    };

    console.log('[Production Check] Environment validation:', checks);

    if (!checks.https) {
      throw new Error('FCM requires HTTPS in production');
    }

    if (!checks.serviceWorkerSupport) {
      throw new Error('Service Worker not supported');
    }

    if (!checks.notificationSupport) {
      throw new Error('Notifications not supported');
    }

    if (!checks.pushManagerSupport) {
      throw new Error('Push Manager not supported');
    }

    return true;
  }, []);

  const registerTokenWithBackend = useCallback(
    async (webToken) => {
      console.log('[Hook] Registering token with backend...');
      
      try {
        const response = await fetch(`${baseUrl}/user/updateFCMWeb`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
          body: JSON.stringify({ token: webToken }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(`Backend registration failed: ${response.status} - ${errorData.message || 'Unknown error'}`);
        }

        const data = await response.json();
        console.log('[Hook] Token registered successfully:', data);
        return data;
      } catch (error) {
        console.error('[Hook] Backend registration error:', error);
        throw error;
      }
    },
    [userToken, baseUrl]
  );

  const getNotificationId = useCallback((payload) => {
    const data = payload.data || {};
    return (
      data.id ||
      data.notificationId ||
      `${data.type || "default"}-${data.createdBy || "unknown"}-${Date.now()}`
    );
  }, []);

  const navigateToRoute = useCallback((targetRoute) => {
    if (targetRoute) {
      console.log('[Hook] Navigating to route:', targetRoute);
      if (window.history && window.history.pushState) {
        window.history.pushState({}, '', targetRoute);
        window.dispatchEvent(new PopStateEvent('popstate'));
      } else {
        window.location.href = targetRoute;
      }
    }
  }, []);

  const handleForegroundMessage = useCallback(
    (payload) => {
      console.log('[Hook] Foreground message received:', payload);

      const notificationId = getNotificationId(payload);
      if (processedNotificationsRef.current.has(notificationId)) {
        console.log('[Hook] Duplicate notification blocked:', notificationId);
        return;
      }

      processedNotificationsRef.current.add(notificationId);
      if (processedNotificationsRef.current.size > 50) {
        processedNotificationsRef.current.clear();
      }

      setNotification(payload);
    },
    [getNotificationId]
  );

  // SINGLE initializeNotifications function with all the fixes
  const initializeNotifications = useCallback(async () => {
    if (initializationAttempted.current) {
      console.log('[Hook] Notification initialization already attempted');
      return;
    }

    initializationAttempted.current = true;

    try {
      setIsLoading(true);
      setError(null);

      if (!userToken) {
        throw new Error("User not authenticated");
      }

      // Validate production environment
      validateProductionEnvironment();

      console.log('[Hook] Starting notification initialization...');
      
      // Add delay for production stability
      if (process.env.NODE_ENV === 'production') {
        console.log('[Hook] Production environment detected, adding stability delay...');
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
      
      const fcmToken = await requestNotificationPermission(handleForegroundMessage);

      if (fcmToken) {
        setToken(fcmToken);
        console.log('[Hook] FCM Token received:', fcmToken.substring(0, 50) + '...');

        try {
          await registerTokenWithBackend(fcmToken);
          console.log('[Hook] Backend registration completed');
        } catch (backendError) {
          console.error('[Hook] Backend registration failed:', backendError);
          // Don't throw here - token is still valid for notifications
        }

      } else {
        throw new Error("Failed to get FCM token");
      }
    } catch (err) {
      console.error('[Hook] Notification initialization error:', err);
      setError(err.message);
      // Reset the flag so user can try again
      initializationAttempted.current = false;
    } finally {
      setIsLoading(false);
    }
  }, [userToken, registerTokenWithBackend, handleForegroundMessage, validateProductionEnvironment]);

  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      console.log('[Hook] Service worker message received:', event.data);

      const { type, action, route, data } = event.data;

      if (type === 'NOTIFICATION_VIEW_CLICK' || type === 'NOTIFICATION_DEFAULT_CLICK') {
        console.log('[Hook] View/Default click - navigating to:', route);
        navigateToRoute(route);

      } else if (type === 'NOTIFICATION_OPEN_CLICK') {
        console.log('[Hook] Open App button clicked - going to dashboard');
        navigateToRoute('/dashboard');

      } else if (type === "NOTIFICATION_CLICK") {
        const targetRoute = route || "/dashboard";
        console.log('[Hook] Legacy notification click - navigating to:', targetRoute);
        navigateToRoute(targetRoute);

      } else if (
        type === "MANDOOBEE_NOTIFICATION_CLICK" ||
        type === "NAVIGATE_TO_ROUTE" ||
        type === "MANDOOBEE_NOTIFICATION_ACTION"
      ) {
        const targetRoute = route || data?.screen;
        if (targetRoute) {
          console.log('[Hook] Legacy navigation to:', targetRoute);
          navigateToRoute(targetRoute);
        }
      }

      window.focus();
    };

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", handleServiceWorkerMessage);
      return () => {
        navigator.serviceWorker.removeEventListener("message", handleServiceWorkerMessage);
      };
    }
  }, [navigateToRoute]);

  useEffect(() => {
    if (userToken && !initializationAttempted.current) {
      console.log('[Hook] User authenticated, initializing notifications...');
      setTimeout(() => {
        initializeNotifications();
      }, 2000);
    } else if (!userToken) {
      setIsLoading(false);
      initializationAttempted.current = false;
      setToken(null);
    }
  }, [userToken, initializeNotifications]);

  const refreshToken = useCallback(async () => {
    try {
      console.log('[Hook] Manual token refresh...');
      setIsLoading(true);
      setError(null);
      localStorage.removeItem('fcm_token');
      initializationAttempted.current = false;
      await initializeNotifications();
    } catch (err) {
      setError(err.message);
      console.error('[Hook] Token refresh failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [initializeNotifications]);

  const clearNotification = useCallback(() => {
    setNotification(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const getNotificationStatus = useCallback(() => {
    return {
      hasPermission: Notification.permission === "granted",
      hasToken: !!token,
      isSupported: "Notification" in window && "serviceWorker" in navigator,
      currentPermission: Notification.permission,
      error: error,
      currentToken: token ? token.substring(0, 50) + '...' : null,
      storedToken: localStorage.getItem('fcm_token') ? localStorage.getItem('fcm_token').substring(0, 50) + '...' : null,
      isInitialized: initializationAttempted.current,
      isLoading,
      environment: process.env.NODE_ENV,
      protocol: window.location.protocol
    };
  }, [token, error, isLoading]);

  const enableNotifications = useCallback(async () => {
    if (!userToken) {
      return false;
    }

    try {
      setIsLoading(true);
      setError(null);
      initializationAttempted.current = false;
      await initializeNotifications();
      return true;
    } catch (err) {
      console.error('[Hook] Failed to enable notifications:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [userToken, initializeNotifications]);

  // Force reset function for debugging
  const forceReset = useCallback(() => {
    console.log('[Hook] Force reset called');
    initializationAttempted.current = false;
    setToken(null);
    setError(null);
    setIsLoading(false);
    localStorage.removeItem('fcm_token');
  }, []);

  return {
    token,
    notification,
    isLoading,
    error,
    hasPermission: token !== null,
    refreshToken,
    clearNotification,
    clearError,
    getNotificationStatus,
    enableNotifications,
    navigateToRoute,
    forceReset, 
  };
};

export default useNotifications;